import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { PriceListUsage, UserLimits, UserTariffPurchase, UserTariffRequest } from "../../api/user";
import { fetchUserTariffHistory, getUserPurchases } from "../../api/user";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { useCurrentUserInfo } from "../../hooks/useCurrentUserInfo";
import {
  formatCheckPacksSummary,
  formatLimitsDelta,
  formatPurchaseDate,
  formatPurchasePrice,
  formatPurchaseStatus,
  formatPurchaseTitle,
  formatRequestedPurchaseTitle,
  formatUserLimitsPeriodRange,
  getPurchaseTypeLabel,
  formatPaymentDateTime,
  getRequestedTariffBadge,
  isPaymentUrlExpired,
  getUserLimitsEntitlementRows,
  getUserLimitsStatusBadge,
  getUserLimitsUsageRows,
} from "../../utils/userLimitsFormat";
import {
  formatTariffDate,
  formatTariffPeriodRange,
  formatTariffPrice,
  formatTariffSiteAmount,
  getTariffUsageRemainderRows,
} from "../../utils/userTariffFormat";
import {
  getProfileTariffBadge,
  getProfileTariffTitle,
  getTariffTimelineStatus,
  getVisibleTariffs,
} from "../../utils/tariffTimeline";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import "./ProfileTariffSection.scss";

type ProfileTariffTab = "limits" | "history";

const ProfileTariffInfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="profile-tariff-row">
    <dt className="profile-tariff-label">{label}</dt>
    <dd className="profile-tariff-value">{value}</dd>
  </div>
);

const LegacyTariffBlock = ({
  tariff,
  badge,
  showUsageRemainders,
  showPeriod = true,
}: {
  tariff: PriceListUsage;
  badge?: string;
  showUsageRemainders?: boolean;
  showPeriod?: boolean;
}) => {
  const status = getTariffTimelineStatus(tariff);
  const usageRows = showUsageRemainders ? getTariffUsageRemainderRows(tariff) : [];

  return (
    <div className="profile-tariff-block">
      <div className="profile-tariff-block-head">
        <h3 className="profile-tariff-block-title">{getProfileTariffTitle(tariff, status)}</h3>
        {badge ? (
          <span
            className={`profile-tariff-badge${
              status === "active" ? " profile-tariff-badge--active" : ""
            }`}
          >
            {badge}
          </span>
        ) : null}
      </div>
      <dl className="profile-tariff-list">
        <ProfileTariffInfoRow label="Название" value={tariff.tariffName?.trim() || "—"} />
        <ProfileTariffInfoRow label="Стоимость" value={formatTariffPrice(tariff)} />
        <ProfileTariffInfoRow label="Количество сайтов" value={formatTariffSiteAmount(tariff)} />
        {showPeriod ? (
          <ProfileTariffInfoRow label="Период действия" value={formatTariffPeriodRange(tariff)} />
        ) : null}
        {usageRows.map((row) => (
          <ProfileTariffInfoRow key={row.label} label={row.label} value={row.value} />
        ))}
      </dl>
    </div>
  );
};

const UserLimitsBlock = ({ limits }: { limits: UserLimits }) => {
  const badge = getUserLimitsStatusBadge(limits);
  const usageRows = getUserLimitsUsageRows(limits);
  const entitlementRows = getUserLimitsEntitlementRows(limits);
  const isActive = badge === "Активен";

  return (
    <div className="profile-tariff-block">
      <div className="profile-tariff-block-head">
        <h3 className="profile-tariff-block-title">Текущий период</h3>
        <span
          className={`profile-tariff-badge${isActive ? " profile-tariff-badge--active" : ""}`}
        >
          {badge}
        </span>
      </div>
      <dl className="profile-tariff-list">
        <ProfileTariffInfoRow label="Период" value={formatUserLimitsPeriodRange(limits)} />
        {usageRows.length === 0 ? (
          <ProfileTariffInfoRow label="Лимиты" value="Нет данных по лимитам" />
        ) : (
          usageRows.map((row) => (
            <ProfileTariffInfoRow key={row.key} label={row.label} value={row.value} />
          ))
        )}
      </dl>

      {entitlementRows.length > 0 ? (
        <div className="profile-tariff-entitlements">
          <p className="profile-tariff-entitlements-title">Доступные типы проверок</p>
          <ul className="profile-tariff-entitlements-list">
            {entitlementRows.map((row) => (
              <li key={row.key} className="profile-tariff-entitlement-item">
                <span className="profile-tariff-entitlement-name">{row.label}</span>
                <span className="profile-tariff-entitlement-meta">
                  {[
                    row.categoryCount > 0 ? `категорий: ${row.categoryCount}` : null,
                    ...row.options,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "без доп. опций"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const RequestedBlock = ({ request }: { request: UserTariffRequest | PriceListUsage }) => {
  const badge = getRequestedTariffBadge(request);
  const isPending = badge === "Ожидает оплаты";
  const siteAmount =
    "siteAmount" in request ? formatTariffSiteAmount(request as PriceListUsage) : "—";
  const paymentUrl = request.paymentUrl?.trim() ?? "";
  const paymentUrlExpiration =
    "paymentUrlExpiration" in request ? request.paymentUrlExpiration : undefined;
  const paymentExpired = Boolean(paymentUrl) && isPaymentUrlExpired(paymentUrlExpiration);
  const canPay = Boolean(paymentUrl) && !paymentExpired;

  return (
    <div className="profile-tariff-block">
      <div className="profile-tariff-block-head">
        <h3 className="profile-tariff-block-title">{formatRequestedPurchaseTitle(request)}</h3>
        <span
          className={`profile-tariff-badge${
            isPending ? " profile-tariff-badge--pending" : ""
          }`}
        >
          {badge}
        </span>
      </div>
      <dl className="profile-tariff-list">
        <ProfileTariffInfoRow
          label="Тип"
          value={request.tariffType === "checks" ? "Докупка" : "Тариф"}
        />
        <ProfileTariffInfoRow
          label="Стоимость"
          value={formatTariffPrice(request as PriceListUsage)}
        />
        {request.tariffType === "checks" ? (
          <ProfileTariffInfoRow
            label="Состав"
            value={formatCheckPacksSummary(request.checkPacks)}
          />
        ) : (
          <ProfileTariffInfoRow label="Количество сайтов" value={siteAmount} />
        )}
        {paymentUrl ? (
          <ProfileTariffInfoRow
            label="Ссылка действует до"
            value={formatPaymentDateTime(paymentUrlExpiration)}
          />
        ) : null}
      </dl>
      {canPay ? (
        <a className="profile-tariff-pay-btn" href={paymentUrl}>
          Оплатить
        </a>
      ) : null}
      {paymentExpired ? (
        <p className="profile-tariff-pay-note">
          Ссылка на оплату истекла.{" "}
          <Link to="/tariffs">Оформите заявку заново</Link>
        </p>
      ) : null}
    </div>
  );
};

const PurchaseHistoryBlock = ({ purchase }: { purchase: UserTariffPurchase }) => (
  <div className="profile-tariff-block">
    <div className="profile-tariff-block-head">
      <h3 className="profile-tariff-block-title">{formatPurchaseTitle(purchase)}</h3>
      <span className="profile-tariff-badge">{getPurchaseTypeLabel(purchase)}</span>
    </div>
    <dl className="profile-tariff-list">
      <ProfileTariffInfoRow label="Дата" value={formatPurchaseDate(purchase)} />
      <ProfileTariffInfoRow label="Стоимость" value={formatPurchasePrice(purchase)} />
      {purchase.tariffType === "checks" ? (
        <ProfileTariffInfoRow
          label="Состав"
          value={formatCheckPacksSummary(purchase.checkPacks)}
        />
      ) : (
        <ProfileTariffInfoRow label="Лимиты" value={formatLimitsDelta(purchase.limitsDelta)} />
      )}
      {purchase.status ? (
        <ProfileTariffInfoRow label="Статус" value={formatPurchaseStatus(purchase.status)} />
      ) : null}
    </dl>
  </div>
);

export const ProfileTariffSection = () => {
  const {
    uid,
    activeTariffs,
    requestedTariffs,
    userLimits,
    userInfo,
    loading,
    error,
  } = useCurrentUserInfo();
  const [tab, setTab] = useState<ProfileTariffTab>("limits");
  const [history, setHistory] = useState<UserTariffPurchase[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const visibleTariffs = getVisibleTariffs(activeTariffs);
  const hasUserLimits = Boolean(
    userLimits &&
      (userLimits.periodStart ||
        userLimits.periodEnd ||
        userLimits.limits ||
        userLimits.usage ||
        userLimits.entitlements)
  );
  const hasRequested = requestedTariffs.length > 0;
  const hasLegacyTariffs = !hasUserLimits && visibleTariffs.length > 0;
  const hasAnyCurrent = hasUserLimits || hasLegacyTariffs || hasRequested;

  const loadHistory = useCallback(() => {
    const embeddedHistory = getUserPurchases(userInfo);
    if (Array.isArray(embeddedHistory)) {
      setHistory(embeddedHistory);
      setHistoryError(null);
      setHistoryLoaded(true);
      setHistoryLoading(false);
      return;
    }

    if (uid == null) {
      setHistory([]);
      setHistoryError("Не удалось определить пользователя");
      setHistoryLoaded(true);
      setHistoryLoading(false);
      return;
    }

    setHistoryLoading(true);
    setHistoryError(null);
    fetchUserTariffHistory(uid)
      .then((items) => {
        setHistory(items);
        setHistoryLoaded(true);
      })
      .catch((err: unknown) => {
        setHistory([]);
        setHistoryError(getApiErrorMessage(err, "Не удалось загрузить историю покупок"));
        setHistoryLoaded(true);
      })
      .finally(() => setHistoryLoading(false));
  }, [uid, userInfo]);

  useEffect(() => {
    if (tab !== "history" || historyLoaded) return;
    loadHistory();
  }, [historyLoaded, loadHistory, tab]);

  return (
    <section className="profile-card" aria-label="Тарифы и лимиты">
      <div className="profile-tariff-section-head">
        <h2 className="profile-card-title">Тарифы и лимиты</h2>
        <div className="profile-tariff-tabs" role="tablist" aria-label="Разделы тарифов">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "limits"}
            className={`profile-tariff-tab${tab === "limits" ? " profile-tariff-tab--active" : ""}`}
            onClick={() => setTab("limits")}
          >
            Текущий период
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "history"}
            className={`profile-tariff-tab${tab === "history" ? " profile-tariff-tab--active" : ""}`}
            onClick={() => setTab("history")}
          >
            История покупок
          </button>
        </div>
      </div>

      {loading ? (
        <div className="profile-tariff-loading">
          <CircularProgress size="md" />
        </div>
      ) : (
        <div className="profile-tariff-sections">
          {error ? <p className="profile-tariff-error">{error}</p> : null}

          {tab === "limits" ? (
            <>
              {!hasAnyCurrent ? (
                <p className="profile-tariff-empty">
                  На данный момент у вас нет активного периода и заявок.
                </p>
              ) : null}

              {hasUserLimits && userLimits ? <UserLimitsBlock limits={userLimits} /> : null}

              {hasLegacyTariffs
                ? visibleTariffs.map((tariff, index) => {
                    const status = getTariffTimelineStatus(tariff);
                    return (
                      <LegacyTariffBlock
                        key={`${tariff.pricelistId ?? "tariff"}-${tariff.startAt ?? index}`}
                        tariff={tariff}
                        badge={getProfileTariffBadge(tariff, status)}
                        showUsageRemainders={status === "active"}
                      />
                    );
                  })
                : null}

              {hasRequested ? (
                <div className="profile-tariff-requests">
                  <h3 className="profile-tariff-requests-title">Заявки</h3>
                  {requestedTariffs.map((request, index) => (
                    <RequestedBlock
                      key={`${request.payId ?? request.pricelistId ?? "request"}-${index}`}
                      request={request}
                    />
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <>
              {historyLoading ? (
                <div className="profile-tariff-loading">
                  <CircularProgress size="md" />
                </div>
              ) : null}
              {historyError ? <p className="profile-tariff-error">{historyError}</p> : null}
              {!historyLoading && !historyError && history.length === 0 ? (
                <p className="profile-tariff-empty">История покупок пока пуста.</p>
              ) : null}
              {history.map((purchase, index) => (
                <PurchaseHistoryBlock
                  key={`${purchase.payId ?? purchase.purchasedAt ?? purchase.createdAt ?? "purchase"}-${index}`}
                  purchase={purchase}
                />
              ))}
            </>
          )}

          <Link to="/tariffs" className="btn btn-primary profile-tariff-action">
            {hasAnyCurrent || history.length > 0 ? "Посмотреть тарифы" : "Выбрать тариф"}
          </Link>
        </div>
      )}
    </section>
  );
};
